import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
import { CandleFlame, OpenJournal, SacredCircle, FamilyTree } from './SacredIcons';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/kbtKC1SqAKnPbBnYCSTnoL/mokup_contelaciones_sin_fondo.png',
  alt: 'Kit Constelaciones Familiares — Sistema profesional completo'
};
export const Hero = () => {
  return <div className="relative overflow-hidden pb-8 md:pt-20 md:pb-12 bg-transparent pt-[10px]" data-id="element-118">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-violetLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-119"></div>

      {/* Floating Sacred decor */}
      <CandleFlame className="hidden md:block absolute top-32 left-[6%] w-8 h-12 opacity-30 animate-float-soft -z-10" style={{
      transform: 'rotate(5deg)'
    }} data-id="element-120" />
      <SacredCircle className="hidden md:block absolute top-48 right-[8%] w-24 h-24 opacity-20 animate-float-soft -z-10" style={{
      transform: 'rotate(-15deg)',
      animationDelay: '1.2s'
    }} data-id="element-121" />
      <OpenJournal className="hidden md:block absolute bottom-40 left-[4%] w-20 h-16 opacity-30 animate-float-soft -z-10" style={{
      animationDelay: '2s'
    }} data-id="element-122" />
      <FamilyTree className="hidden md:block absolute bottom-32 right-[5%] w-12 h-12 opacity-40 animate-float-soft -z-10" style={{
      transform: 'rotate(15deg)',
      animationDelay: '0.6s'
    }} data-id="element-123" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-124">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-125">
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-gold/30 px-4 py-1.5 rounded-full mb-6 gap-2 shadow-sm pt-[2px] pb-[2px] pl-[10px] pr-[10px]" data-id="element-126">
            <span className="text-sm" data-id="element-127">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-violetDark" data-id="element-128">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="relative inline-block mb-4 max-w-4xl mx-auto" data-id="element-129">
            {/* Ambient glow behind headline */}
            <div className="absolute inset-0 -z-10 blur-3xl opacity-60 pointer-events-none" data-id="element-130">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[140%] bg-gradient-to-r from-brand-violet/20 via-brand-gold/15 to-brand-burgundy/20 rounded-full" data-id="element-131" />
            </div>

            {/* Sparkles */}
            <span className="sparkle absolute -top-2 left-[8%] text-brand-goldDark text-sm md:text-xl" style={{
            animationDelay: '0s'
          }} data-id="element-132">
              ✦
            </span>
            <span className="sparkle absolute top-1 right-[6%] text-brand-violetLight text-xs md:text-lg" style={{
            animationDelay: '0.8s'
          }} data-id="element-133">
              ✧
            </span>
            <span className="sparkle absolute -bottom-1 left-[20%] text-brand-gold text-xs md:text-base" style={{
            animationDelay: '1.6s'
          }} data-id="element-134">
              ✦
            </span>
            <span className="sparkle absolute bottom-2 right-[18%] text-brand-burgundy text-sm md:text-lg" style={{
            animationDelay: '2.2s'
          }} data-id="element-135">
              ✧
            </span>

            <h1 className="sm:text-4xl md:text-6xl lg:text-7xl text-[28px] font-[700] leading-tight tracking-tight" style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-136">
              <span className="headline-shine" data-id="element-137">Procesos más profundos</span>
              <br data-id="element-138" />
              <span className="phrase-glow font-[800]" data-id="element-139">a partir de hoy</span>
              <span className="text-brand-violetDark" data-id="element-140">.</span>
            </h1>
          </div>
          <p className="md:text-2xl text-brand-goldDark mb-8 font-bold font-serif text-[18px]" data-id="element-141">
            Planillas listas para usar.
          </p>

          {/* Hero Image */}
          <div className="relative z-10 w-full max-w-[320px] sm:max-w-md md:max-w-lg lg:max-w-xl mx-auto mb-2 flex justify-center" data-id="element-142">
            <div className="relative overflow-hidden rounded-2xl bg-transparent w-full mx-auto" style={{
            aspectRatio: '1/1',
            maxHeight: '500px'
          }} data-id="element-143">
              <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-contain mx-auto drop-shadow-2xl" draggable={false} data-id="element-144" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 mt-8 space-y-3 px-2" data-id="element-145">
            <p className="md:text-xl font-bold text-brand-violet font-serif text-[13px] leading-relaxed" data-id="element-146">
              Kit Constelaciones Familiares — Sistema profesional completo
            </p>
            <p className="md:text-base text-brand-text/80 font-sans text-[12px]" data-id="element-147">
              El sistema N°1 para terapeutas en Constelaciones Familiares en
              Argentina — Todos los niveles y tipos de proceso.
            </p>
            <p className="md:text-base text-brand-text/80 font-sans text-[12px]" data-id="element-148">
              Protocolos + herramientas clínicas + fichas de paciente + frases
              sanadoras + práctica ordenada listos para usar.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-12 text-xs md:text-sm font-medium px-2" data-id="element-149">
            <div className="inline-flex items-center bg-white px-3 py-2 rounded-full border border-brand-violet/20 shadow-sm text-[10px] md:text-xs" data-id="element-150">
              <BookOpen className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-violet shrink-0" data-id="element-151" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white px-3 py-2 rounded-full border border-brand-violet/20 shadow-sm text-[10px] md:text-xs" data-id="element-152">
              <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-violet shrink-0" data-id="element-153" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white px-3 py-2 rounded-full border border-brand-violet/20 shadow-sm text-[10px] md:text-xs" data-id="element-154">
              <span className="mr-1.5 text-brand-violet shrink-0" data-id="element-155">🌿</span>
              Terapeutas formados
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-156">
            <CTAButton variant="purple" className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5 cta-shimmer relative overflow-hidden" data-id="element-157">
              <span className="relative z-10" data-id="element-158">
                🌿 → QUIERO MI KIT CONSTELACIONES AHORA
              </span>
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-159">
              <div className="flex items-center" data-id="element-160">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-161" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center" data-id="element-162">📥 Descarga inmediata</div>
              <div className="flex items-center" data-id="element-163">
                🖨️ Listo para imprimir o usar en tablet
              </div>
              <div className="flex items-center" data-id="element-164">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-165" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center" data-id="element-166">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-goldDark" data-id="element-167" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-12 border-y border-brand-violet/15 bg-white/60 py-4 backdrop-blur-sm" data-id="element-168">
        <div className="md:hidden flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm pl-[16px] pr-[16px]" data-id="element-169">
          <div className="flex items-center" data-id="element-170">
            <span className="font-bold text-brand-violet mr-1.5" data-id="element-171">+200</span>{' '}
            terapeutas
          </div>
          <div className="flex items-center" data-id="element-172">
            <span className="font-bold text-brand-violet mr-1.5" data-id="element-173">5</span> Tomos
          </div>
          <div className="flex items-center" data-id="element-174">
            <span className="font-bold text-brand-violet mr-1.5" data-id="element-175">3</span> Bonus
          </div>
          <div className="flex items-center" data-id="element-176">
            <Star className="w-3.5 h-3.5 text-brand-goldDark fill-brand-goldDark mr-1" data-id="element-177" />{' '}
            4.9/5
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm" data-id="element-178">
          <div className="flex items-center" data-id="element-179">
            <span className="font-bold text-brand-violet mr-2" data-id="element-180">+200</span>{' '}
            terapeutas equipados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-181"></div>
          <div className="flex items-center" data-id="element-182">
            <span className="font-bold text-brand-violet mr-2" data-id="element-183">5</span> Tomos
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-184"></div>
          <div className="flex items-center" data-id="element-185">
            <span className="font-bold text-brand-violet mr-2" data-id="element-186">3</span> Bonus
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold/50 shrink-0" data-id="element-187"></div>
          <div className="flex items-center" data-id="element-188">
            <Star className="w-4 h-4 text-brand-goldDark fill-brand-goldDark mr-1" data-id="element-189" />{' '}
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};